import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useTheme } from '@/contexts/ThemeContext';
import { handleApiError } from '../../services/api';
import {
  semanticsApi,
  type SemanticEntityView,
  type SemanticVersionEntry,
} from '../../services/semantics-api';

/**
 * Version history for a semantic definition, over the append-only
 * semantic_entity_versions table (proposed → confirmed → superseded / discarded).
 * Rows expand to a client-side JSON diff against the prior version. Revert lands
 * with the Phase 3 write endpoint.
 */
const STATUS_META: Record<SemanticVersionEntry['status'], { label: string; cls: string }> = {
  confirmed: { label: 'Live', cls: 'bg-success/15 text-success border-transparent' },
  proposed: { label: 'Proposed', cls: 'bg-warn/15 text-warn border-transparent' },
  superseded: { label: 'Superseded', cls: 'bg-muted text-muted-foreground border-border' },
  discarded: { label: 'Discarded', cls: 'bg-muted text-muted-foreground border-border line-through' },
};

const HistoryView: React.FC<{ entity: SemanticEntityView }> = ({ entity }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['semanticVersions', entity.id],
    queryFn: () => semanticsApi.getVersions(entity.id),
  });

  const byNumber = useMemo(() => {
    const m = new Map<number, SemanticVersionEntry>();
    for (const v of data?.versions ?? []) m.set(v.versionNumber, v);
    return m;
  }, [data]);

  const restore = useMutation({
    mutationFn: (v: SemanticVersionEntry) =>
      semanticsApi.saveVersion(entity.id, {
        definition: v.definition ?? {},
        change_note: `Restored v${v.versionNumber}`,
      }),
    onSuccess: () => {
      toast.success('Restored — live from the next query');
      void queryClient.invalidateQueries({ queryKey: ['semanticEntity', entity.id] });
      void queryClient.invalidateQueries({ queryKey: ['semanticVersions', entity.id] });
      void queryClient.invalidateQueries({ queryKey: ['semanticsOverview'] });
    },
    onError: (e) => toast.error(handleApiError(e) || 'Restore failed'),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="px-5 py-4">
      <p className="text-[11.5px] text-muted-foreground mb-2.5">
        Every proposal and confirmation is recorded. Click any row for the change vs. the prior
        version{entity.canManage ? ' — or restore an older definition as a new live version' : ''}.
      </p>
      {data.versions.map((v) => (
        <VersionRow
          key={v.id}
          version={v}
          prior={byNumber.get(v.versionNumber - 1) ?? null}
          isCurrent={v.id === data.currentVersionId}
          canRestore={entity.canManage && v.definition != null && v.id !== data.currentVersionId}
          onRestore={() => restore.mutate(v)}
          restoring={restore.isPending}
        />
      ))}
    </div>
  );
};

const VersionRow: React.FC<{
  version: SemanticVersionEntry;
  prior: SemanticVersionEntry | null;
  isCurrent: boolean;
  canRestore: boolean;
  onRestore: () => void;
  restoring: boolean;
}> = ({ version, prior, isCurrent, canRestore, onRestore, restoring }) => {
  const [expanded, setExpanded] = useState(false);
  const { isDark } = useTheme();
  const status = STATUS_META[version.status];

  return (
    <div className="rounded-[9px] border border-border mb-2 overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded((x) => !x)}
        className="flex items-center gap-2.5 px-3.5 py-2.5 text-[12.5px] cursor-pointer hover:bg-muted/40"
      >
        <span
          className={clsx(
            'text-[10px] font-bold rounded-[5px] px-1.5 py-px flex-none border',
            isCurrent ? 'bg-success/15 text-success border-transparent' : 'bg-muted text-foreground border-border',
          )}
        >
          v{version.versionNumber}
        </span>
        <span className={clsx('text-[9.5px] font-bold rounded-[5px] px-1.5 py-px flex-none border', status.cls)}>
          {status.label}
        </span>
        <span className="inline-flex items-center gap-1.5 font-semibold text-[12px] flex-none">
          <span
            className={clsx(
              'w-4 h-4 rounded flex items-center justify-center text-[8px]',
              version.actorType === 'user'
                ? 'rounded-full bg-primary text-primary-foreground'
                : 'bg-muted border border-border',
            )}
          >
            {version.actorType === 'user' ? initials(version.actorName) : version.actorType === 'llm' ? '✦' : '⚙'}
          </span>
          {version.actorName}
        </span>
        <span className="flex-1 min-w-0 truncate text-[12px] text-muted-foreground">
          {version.changeNote ?? '—'}
        </span>
        <span className="text-[10.5px] text-muted-foreground tabular-nums flex-none">
          {new Date(version.createdAt).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
        {canRestore && (
          <button
            type="button"
            disabled={restoring}
            onClick={(e) => {
              e.stopPropagation();
              onRestore();
            }}
            className="text-[10.5px] font-semibold rounded-[7px] px-2.5 py-0.5 border border-input text-muted-foreground hover:text-foreground disabled:opacity-50 flex-none"
          >
            Restore
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-dashed border-border text-[11px] overflow-x-auto">
          <ReactDiffViewer
            oldValue={pretty(prior?.definition)}
            newValue={pretty(version.definition)}
            splitView={false}
            compareMethod={DiffMethod.WORDS}
            useDarkTheme={isDark}
            hideLineNumbers
          />
        </div>
      )}
    </div>
  );
};

function pretty(def: Record<string, unknown> | null | undefined): string {
  return def ? JSON.stringify(def, null, 2) : '';
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

export default HistoryView;
